import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { T, sans } from './VendorDetails';

/* The vendor's document library: everything filed for this (tenant, vendor) relationship, whether
   the vendor uploaded it while answering a questionnaire or a team member filed it here. One shared
   library (database_pg/vendor_evidence.py) -- a document filed here can be attached to questionnaire
   answers later, and vice versa.

   What the page adds on top of a plain file list:
   - lifecycle: issue/expiry dates -> Valid / Expiring soon (60 days) / Expired / No expiry
   - a key-document checklist (SOC 2, ISO 27001, pen test, insurance, policies, BCP) showing what's
     on file, what's lapsing and what's missing -- a deterministic lookup on document type
   - where each document is used as questionnaire evidence, with the AI evidence check's verdict
   - in-page preview (PDF / image / text), metadata editing, team uploads. */

interface Usage {
  response_id: string;
  item_id: string;
  linked_at: string;
  response_status: string;
  sent_at: string;
  questionnaire_name: string;
  question_text: string;
  answer_value: string | null;
  verdict: string | null;
  confidence: number | null;
  rationale: string | null;
}

interface Doc {
  id: string;
  file_name: string;
  content_type: string | null;
  size_bytes: number | null;
  document_type: string | null;
  description: string | null;
  created_at: string;
  issued_at: string | null;
  expires_at: string | null;
  updated_at: string | null;
  security_flags: string | null; // accepted active content, e.g. 'JavaScript, attached files' -- see services/upload_security.py
  uploaded_by_user_id: string | null;
  source: 'vendor' | 'team' | 'public';
  uploaded_by_name: string | null;
  source_url?: string | null; // only set for auto-discovered ('public') documents -- see services/public_documents.py
  usage: Usage[];
}

interface Props { vendorId: string; isDark: boolean }

type StatusKey = 'valid' | 'expiring' | 'expired' | 'none';

const APP_HEADER_HEIGHT = 64;
const PANEL_MS = 180;
const EXPIRING_DAYS = 60;
const MAX_SIZE = 15 * 1024 * 1024;
const ALLOWED_EXT = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.txt']; // must match services/upload_security.py
const FALLBACK_TYPES = ['SOC 2 Report', 'ISO 27001 Certificate', 'Penetration Test Report', 'Security Policy', 'Privacy Policy',
  'Business Continuity Plan', 'Insurance Certificate', 'Audit Report', 'Data Processing Agreement', 'Contract', 'NDA', 'Other'];

// The documents a TPRM review normally expects to see on file for a vendor.
const KEY_DOCS: { type: string; hint: string }[] = [
  { type: 'SOC 2 Report', hint: 'Independent audit of security controls' },
  { type: 'ISO 27001 Certificate', hint: 'Certified security management system' },
  { type: 'Penetration Test Report', hint: 'Recent third-party security test' },
  { type: 'Insurance Certificate', hint: 'Cyber / liability coverage' },
  { type: 'Security Policy', hint: 'Information security policy' },
  { type: 'Privacy Policy', hint: 'How personal data is handled' },
  { type: 'Business Continuity Plan', hint: 'Disaster recovery & continuity' },
];

// How long each document type is normally treated as current, used only to *suggest* an expiry.
const VALIDITY_MONTHS: Record<string, number> = {
  'SOC 2 Report': 12, 'Penetration Test Report': 12, 'Insurance Certificate': 12, 'Audit Report': 12, 'ISO 27001 Certificate': 36,
};

const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};
const parseDay = (s: string) => { const [y, m, d] = s.slice(0, 10).split('-').map(Number); return new Date(y, m - 1, d); };
const today = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); };
const daysUntil = (s: string) => Math.round((parseDay(s).getTime() - today().getTime()) / 86400000);
const fmtDay = (s: string | null) => (s ? parseDay(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addMonths = (s: string, months: number) => { const d = parseDay(s); d.setMonth(d.getMonth() + months); return isoDay(d); };
const fmtSize = (bytes: number | null) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const extOf = (name: string) => { const i = name.lastIndexOf('.'); return i >= 0 ? name.slice(i).toLowerCase() : ''; };
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function guessType(fileName: string): string {
  const n = fileName.toLowerCase();
  if (/soc\s?-?2|soc\s?ii/.test(n)) return 'SOC 2 Report';
  if (/iso|27001/.test(n)) return 'ISO 27001 Certificate';
  if (/pen(etration)?[\s_-]?test|pentest/.test(n)) return 'Penetration Test Report';
  if (/insurance|coi\b|liability/.test(n)) return 'Insurance Certificate';
  if (/privacy/.test(n)) return 'Privacy Policy';
  if (/bcp|continuity|disaster|\bdr\b/.test(n)) return 'Business Continuity Plan';
  if (/dpa|data[\s_-]?processing/.test(n)) return 'Data Processing Agreement';
  if (/nda|non[\s_-]?disclosure/.test(n)) return 'NDA';
  if (/contract|msa|agreement/.test(n)) return 'Contract';
  if (/audit/.test(n)) return 'Audit Report';
  if (/policy|security/.test(n)) return 'Security Policy';
  return 'Other';
}

const pal = (isDark: boolean) => ({
  dark: isDark,
  ink: isDark ? '#e5e7eb' : T.ink,
  slate: isDark ? '#94a3b8' : T.slate,
  muted: isDark ? '#64748b' : T.muted,
  line: isDark ? '#1f1f1f' : T.bdr,
  lineStrong: isDark ? '#2a2a2a' : '#CBD5E1',
  surface: isDark ? '#0d0d0d' : T.surface,
  surface2: isDark ? '#141414' : T.surface2,
  hover: isDark ? '#151515' : '#F8FAFC',
  primary: isDark ? '#3B82F6' : '#2563EB',
  blue: isDark ? '#60A5FA' : '#2563EB',
  green: isDark ? '#4ADE80' : '#15803D',
  amber: isDark ? '#FBBF24' : '#B45309',
  red: isDark ? '#F87171' : '#DC2626',
  orange: isDark ? '#FB923C' : '#EA580C',
  violet: isDark ? '#A78BFA' : '#7C3AED',
});
type Pal = ReturnType<typeof pal>;
const soft = (p: Pal, c: string) => `${c}${p.dark ? '22' : '14'}`;

function statusOf(doc: Doc, p: Pal): { key: StatusKey; label: string; short: string; color: string } {
  if (!doc.expires_at) return { key: 'none', label: 'No expiry set', short: 'No expiry', color: p.slate };
  const d = daysUntil(doc.expires_at);
  if (d < 0) return { key: 'expired', label: `Expired ${fmtDay(doc.expires_at)}`, short: 'Expired', color: p.red };
  if (d <= EXPIRING_DAYS) return { key: 'expiring', label: d === 0 ? 'Expires today' : `Expires in ${plural(d, 'day')}`, short: 'Expiring soon', color: p.amber };
  return { key: 'valid', label: `Valid until ${fmtDay(doc.expires_at)}`, short: 'Valid', color: p.green };
}

function fileKind(name: string, contentType: string | null) {
  const ext = extOf(name);
  if (ext === '.pdf' || contentType === 'application/pdf') return { label: 'PDF', color: '#DC2626', preview: 'pdf' as const };
  if (['.png', '.jpg', '.jpeg'].includes(ext) || contentType?.startsWith('image/')) return { label: 'IMG', color: '#7C3AED', preview: 'image' as const };
  if (['.doc', '.docx'].includes(ext)) return { label: 'DOC', color: '#2563EB', preview: null };
  if (['.xls', '.xlsx'].includes(ext)) return { label: 'XLS', color: '#16A34A', preview: null };
  if (ext === '.txt') return { label: 'TXT', color: '#64748B', preview: 'text' as const };
  return { label: (ext.slice(1, 4) || 'FILE').toUpperCase(), color: '#64748B', preview: null };
}

const VERDICTS: Record<string, { label: string; tone: 'green' | 'amber' | 'red' | 'slate' }> = {
  matches: { label: 'Supports the answer', tone: 'green' },
  partial: { label: 'Partly supports', tone: 'amber' },
  contradicts: { label: 'Contradicts the answer', tone: 'red' },
  insufficient: { label: 'Not enough to judge', tone: 'slate' },
};

const I = {
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  search: 'M21 21l-4.35-4.35M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z',
  close: 'M6 6l12 12M18 6 6 18',
  check: 'M20 6 9 17l-5-5',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  alert: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6',
  link: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
  edit: 'M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6',
  plus: 'M12 5v14M5 12h14',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  building: 'M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 12v.01M9 15v.01M9 18v.01',
  globe: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20Z',
};
const Icon = ({ d, size = 14, sw = 2 }: { d: string; size?: number; sw?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d={d} /></svg>
);

const btn = (p: Pal, variant: 'primary' | 'secondary' | 'ghost' | 'danger' = 'secondary', size: 'sm' | 'md' = 'md'): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: sans, fontWeight: 600, cursor: 'pointer',
  borderRadius: 7, whiteSpace: 'nowrap', lineHeight: 1.2, fontSize: size === 'sm' ? 12 : 13, padding: size === 'sm' ? '5px 10px' : '8px 14px',
  border: variant === 'secondary' ? `1px solid ${p.line}` : variant === 'danger' ? 'none' : '1px solid transparent',
  background: variant === 'primary' ? p.primary : variant === 'danger' ? p.red : variant === 'secondary' ? p.surface : 'transparent',
  color: variant === 'primary' || variant === 'danger' ? '#fff' : variant === 'ghost' ? p.slate : p.ink,
});

function Pill({ p, color, children, dot = true }: { p: Pal; color: string; children: React.ReactNode; dot?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color, background: soft(p, color), padding: '3px 9px', borderRadius: 99, whiteSpace: 'nowrap' }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: 99, background: color }} />}
      {children}
    </span>
  );
}

function FileBadge({ name, contentType, size = 36 }: { name: string; contentType: string | null; size?: number }) {
  const k = fileKind(name, contentType);
  return (
    <span style={{ width: size, height: size, borderRadius: 8, background: `${k.color}14`, color: k.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: size >= 40 ? 11 : 9.5, fontWeight: 800, letterSpacing: '.03em', fontFamily: sans, border: `1px solid ${k.color}30` }}>
      {k.label}
    </span>
  );
}

async function downloadDoc(vendorId: string, doc: Doc) {
  const res = await api.get(`/v1/vendors/${vendorId}/evidence-documents/${doc.id}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url; a.download = doc.file_name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ───────────────────────────────────────────────────────────────────────────────────────── */

export default function VendorDocuments({ vendorId, isDark }: Props) {
  const p = pal(isDark);
  const { user } = useAuth();
  const canEdit = !!user && user.role !== 'viewer';
  const canDelete = !!user && (user.role === 'admin' || user.role === 'super_admin');
  const docsKey = ['vendors', 'detail', vendorId, 'evidence-documents'];

  const docsQuery = useQuery({
    queryKey: docsKey,
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/evidence-documents`)).data as { documents: Doc[]; document_types: string[] },
  });
  const docs = useMemo(() => docsQuery.data?.documents ?? [], [docsQuery.data]);
  const types = docsQuery.data?.document_types ?? FALLBACK_TYPES;

  // Auto-discovery of genuinely public documents (SEC filings, a vendor's own publicly posted
  // SOC 3 / pentest report) -- never anything behind a login or an email/access-request gate. See
  // services/public_documents.py and CLAUDE.md Phase 53.
  const queryClient = useQueryClient();
  const [detectMsg, setDetectMsg] = useState<string | null>(null);
  const detectMutation = useMutation({
    mutationFn: async () => (await api.post(`/v1/vendors/${vendorId}/public-documents/detect`)).data as
      { found: { title: string }[]; skipped: string[]; checked_sources: string[] },
    onSuccess: (res) => {
      setDetectMsg(res.found.length
        ? `Found ${res.found.length} new public document${res.found.length === 1 ? '' : 's'}: ${res.found.map(f => f.title).join(', ')}`
        : `No new public documents found. Checked: ${res.checked_sources.join('; ')}.`);
      queryClient.invalidateQueries({ queryKey: docsKey });
    },
    onError: (e: any) => setDetectMsg(apiError(e, "Couldn't check for public documents. Try again.")),
  });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusKey>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'vendor' | 'team'>('all');
  const [sort, setSort] = useState<'newest' | 'oldest' | 'expiry' | 'name'>('newest');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const [upload, setUpload] = useState<{ presetType?: string } | null>(null);
  const [deleting, setDeleting] = useState<Doc | null>(null);

  const withStatus = useMemo(() => docs.map(d => ({ doc: d, status: statusOf(d, p) })), [docs, isDark]); // eslint-disable-line react-hooks/exhaustive-deps

  const kpis = useMemo(() => {
    const c = { valid: 0, expiring: 0, expired: 0, none: 0 } as Record<StatusKey, number>;
    withStatus.forEach(x => { c[x.status.key]++; });
    return c;
  }, [withStatus]);

  // For each key document type, the best copy on file: an unexpired one with the latest expiry,
  // otherwise the most recently uploaded.
  const checklist = useMemo(() => KEY_DOCS.map(k => {
    const ofType = withStatus.filter(x => (x.doc.document_type || '').toLowerCase() === k.type.toLowerCase());
    const current = ofType.filter(x => x.status.key !== 'expired')
      .sort((a, b) => (b.doc.expires_at || '9999').localeCompare(a.doc.expires_at || '9999') || b.doc.created_at.localeCompare(a.doc.created_at));
    const best = current[0] || ofType.sort((a, b) => b.doc.created_at.localeCompare(a.doc.created_at))[0];
    return { ...k, best, count: ofType.length };
  }), [withStatus]);
  const keyGaps = checklist.filter(c => !c.best || c.best.status.key === 'expired').length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = withStatus.filter(({ doc, status }) => {
      if (statusFilter !== 'all' && status.key !== statusFilter) return false;
      if (typeFilter !== 'all' && (doc.document_type || 'Untyped') !== typeFilter) return false;
      if (sourceFilter !== 'all' && doc.source !== sourceFilter) return false;
      if (q && ![doc.file_name, doc.document_type, doc.description, doc.uploaded_by_name].some(v => (v || '').toLowerCase().includes(q))) return false;
      return true;
    });
    return [...list].sort((a, b) => {
      if (sort === 'oldest') return a.doc.created_at.localeCompare(b.doc.created_at);
      if (sort === 'name') return a.doc.file_name.localeCompare(b.doc.file_name);
      if (sort === 'expiry') return (a.doc.expires_at || '9999-99').localeCompare(b.doc.expires_at || '9999-99');
      return b.doc.created_at.localeCompare(a.doc.created_at);
    });
  }, [withStatus, search, statusFilter, typeFilter, sourceFilter, sort]);

  const typesInUse = useMemo(() => Array.from(new Set(docs.map(d => d.document_type || 'Untyped'))).sort(), [docs]);
  const filtersActive = statusFilter !== 'all' || typeFilter !== 'all' || sourceFilter !== 'all' || !!search.trim();
  const clearFilters = () => { setStatusFilter('all'); setTypeFilter('all'); setSourceFilter('all'); setSearch(''); };

  const openDoc = (id: string) => { setSelectedId(id); setPanelVisible(true); };
  const closePanel = () => { setPanelVisible(false); setTimeout(() => setSelectedId(cur => (cur && !panelVisibleRef.current ? null : cur)), PANEL_MS); };
  const panelVisibleRef = useRef(panelVisible);
  panelVisibleRef.current = panelVisible;
  const selected = docs.find(d => d.id === selectedId) || null;
  useEffect(() => { if (selectedId && docsQuery.isSuccess && !selected) { setPanelVisible(false); setSelectedId(null); } }, [selectedId, selected, docsQuery.isSuccess]);

  const tile = (key: 'all' | StatusKey | 'gaps', label: string, value: number, color: string, icon: string, sub: string) => {
    const active = key !== 'gaps' && statusFilter === key && key !== 'all';
    return (
      <button key={key} onClick={() => {
        if (key === 'gaps') { document.getElementById('doc-checklist')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
        setStatusFilter(key === statusFilter ? 'all' : key);
      }}
        style={{ textAlign: 'left', cursor: 'pointer', fontFamily: sans, background: p.surface, borderRadius: 10, padding: '14px 16px',
          border: `1px solid ${active ? color : p.line}`, boxShadow: active ? `0 0 0 3px ${soft(p, color)}` : 'none', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: soft(p, color), color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon d={icon} size={16} />
        </span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 22, fontWeight: 700, color: p.ink, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
          <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: p.ink, marginTop: 2 }}>{label}</span>
          <span style={{ display: 'block', fontSize: 11.5, color: p.muted, marginTop: 1 }}>{sub}</span>
        </span>
      </button>
    );
  };

  const selectStyle: React.CSSProperties = {
    padding: '7px 28px 7px 10px', fontSize: 12.5, fontFamily: sans, color: p.ink, background: p.surface, border: `1px solid ${p.line}`, borderRadius: 7, outline: 'none', cursor: 'pointer',
  };
  const th: React.CSSProperties = { textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: p.muted, borderBottom: `1px solid ${p.line}`, whiteSpace: 'nowrap', background: p.surface2 };
  const td: React.CSSProperties = { padding: '12px 14px', fontSize: 12.5, color: p.slate, borderBottom: `1px solid ${p.line}`, verticalAlign: 'middle' };

  return (
    <div style={{ fontFamily: sans, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: p.ink }}>Documents</div>
          <div style={{ fontSize: 12.5, color: p.slate, marginTop: 3, lineHeight: 1.5 }}>
            Certifications, audit reports, policies and contracts for this vendor — uploaded by the vendor while answering a questionnaire, or filed by your team.
          </div>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setDetectMsg(null); detectMutation.mutate(); }} disabled={detectMutation.isPending} style={btn(p, 'secondary')}>
              <Icon d={I.search} /> {detectMutation.isPending ? 'Checking…' : 'Check for public documents'}
            </button>
            <button onClick={() => setUpload({})} style={btn(p, 'primary')}><Icon d={I.upload} /> Upload documents</button>
          </div>
        )}
      </div>
      {detectMsg && (
        <div style={{ fontSize: 12.5, color: p.slate, background: p.surface2, border: `1px solid ${p.line}`, borderRadius: 8, padding: '8px 12px' }}>
          {detectMsg}
        </div>
      )}

      {docsQuery.isPending ? (
        <div style={{ background: p.surface, border: `1px solid ${p.line}`, borderRadius: 10, padding: '48px 0', textAlign: 'center', fontSize: 13, color: p.muted }}>Loading documents…</div>
      ) : docsQuery.isError ? (
        <div style={{ background: p.surface, border: `1px solid ${p.line}`, borderRadius: 10, padding: '48px 16px', textAlign: 'center', fontSize: 13, color: p.red }}>
          {apiError(docsQuery.error, 'Could not load the documents.')}{' '}
          <button onClick={() => docsQuery.refetch()} style={{ ...btn(p, 'ghost', 'sm'), color: p.blue }}>Retry</button>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
            {tile('all', 'Documents on file', docs.length, p.blue, I.file, `${docs.filter(d => d.source === 'vendor').length} from vendor · ${docs.filter(d => d.source === 'team').length} from team`)}
            {tile('valid', 'Valid', kpis.valid, p.green, I.check, 'Within their expiry date')}
            {tile('expiring', 'Expiring soon', kpis.expiring, p.amber, I.clock, `In the next ${EXPIRING_DAYS} days`)}
            {tile('expired', 'Expired', kpis.expired, p.red, I.alert, 'Ask the vendor for a new copy')}
            {tile('gaps', 'Key documents missing', keyGaps, p.orange, I.shield, `Missing or expired, of ${KEY_DOCS.length}`)}
          </div>

          {/* key-document checklist */}
          <div id="doc-checklist" style={{ background: p.surface, border: `1px solid ${p.line}`, borderRadius: 10, padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: p.ink }}>Key document checklist</div>
              <div style={{ fontSize: 12, color: p.muted }}>{KEY_DOCS.length - keyGaps} of {KEY_DOCS.length} on file and current</div>
              <div style={{ flex: 1 }} />
              <div style={{ width: 160, height: 6, borderRadius: 99, background: p.surface2, overflow: 'hidden' }}>
                <div style={{ width: `${((KEY_DOCS.length - keyGaps) / KEY_DOCS.length) * 100}%`, height: '100%', background: p.green, borderRadius: 99 }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 10 }}>
              {checklist.map(c => {
                const st = c.best?.status;
                const color = !c.best ? p.muted : st!.color;
                const label = !c.best ? 'Missing' : st!.key === 'none' ? 'On file · no expiry' : st!.short;
                const clickable = !!c.best || canEdit;
                return (
                  <button key={c.type} disabled={!clickable}
                    onClick={() => (c.best ? openDoc(c.best.doc.id) : setUpload({ presetType: c.type }))}
                    style={{ textAlign: 'left', fontFamily: sans, cursor: clickable ? 'pointer' : 'default', padding: '12px 12px', borderRadius: 9,
                      background: c.best ? p.surface : p.surface2, border: `1px ${c.best ? 'solid' : 'dashed'} ${c.best ? p.line : p.lineStrong}`, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ width: 26, height: 26, borderRadius: 99, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: c.best ? soft(p, color) : 'transparent', color, border: c.best ? 'none' : `1.5px dashed ${p.lineStrong}` }}>
                      <Icon d={!c.best ? I.plus : st!.key === 'expired' ? I.alert : st!.key === 'expiring' ? I.clock : I.check} size={13} sw={2.4} />
                    </span>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: p.ink }}>{c.type}</span>
                      <span style={{ display: 'block', fontSize: 11.5, color, fontWeight: 600, marginTop: 2 }}>
                        {label}{c.best?.doc.expires_at && st!.key !== 'none' ? ` · ${fmtDay(c.best.doc.expires_at)}` : ''}
                      </span>
                      <span style={{ display: 'block', fontSize: 11, color: p.muted, marginTop: 2 }}>
                        {!c.best ? (canEdit ? 'Click to upload' : c.hint) : c.count > 1 ? `${c.count} versions on file` : c.hint}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* library */}
          <div style={{ background: p.surface, border: `1px solid ${p.line}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', borderBottom: `1px solid ${p.line}` }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: p.ink, marginRight: 6 }}>All documents</div>
              <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: p.muted, display: 'inline-flex' }}><Icon d={I.search} size={13} /></span>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, type, uploader…"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px 7px 30px', fontSize: 12.5, fontFamily: sans, color: p.ink, background: p.surface, border: `1px solid ${p.line}`, borderRadius: 7, outline: 'none' }} />
              </div>
              <div style={{ display: 'inline-flex', background: p.surface2, border: `1px solid ${p.line}`, borderRadius: 8, padding: 2 }}>
                {([['all', 'All'], ['valid', 'Valid'], ['expiring', 'Expiring'], ['expired', 'Expired'], ['none', 'No expiry']] as const).map(([k, l]) => {
                  const on = statusFilter === k;
                  return (
                    <button key={k} onClick={() => setStatusFilter(k)} style={{ border: 'none', cursor: 'pointer', fontFamily: sans, fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 6,
                      background: on ? p.surface : 'transparent', color: on ? p.ink : p.slate, boxShadow: on ? '0 1px 2px rgba(15,23,42,.1)' : 'none' }}>
                      {l}{k !== 'all' && <span style={{ marginLeft: 5, color: p.muted, fontWeight: 500 }}>{kpis[k]}</span>}
                    </button>
                  );
                })}
              </div>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={selectStyle} aria-label="Document type">
                <option value="all">All types</option>
                {typesInUse.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value as any)} style={selectStyle} aria-label="Source">
                <option value="all">Any source</option>
                <option value="vendor">From vendor</option>
                <option value="team">From your team</option>
              </select>
              <div style={{ flex: 1 }} />
              <select value={sort} onChange={e => setSort(e.target.value as any)} style={selectStyle} aria-label="Sort">
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="expiry">Expiring soonest</option>
                <option value="name">Name A–Z</option>
              </select>
            </div>

            {docs.length === 0 ? (
              <div style={{ padding: '52px 24px', textAlign: 'center' }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: soft(p, p.blue), color: p.blue, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <Icon d={I.file} size={24} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: p.ink, marginBottom: 6 }}>No documents yet</div>
                <div style={{ fontSize: 12.5, color: p.slate, maxWidth: 440, margin: '0 auto 18px', lineHeight: 1.6 }}>
                  Upload the vendor's SOC 2 report, certificates, policies or contracts here. Anything the vendor attaches while answering a questionnaire lands here automatically too.
                </div>
                {canEdit && <button onClick={() => setUpload({})} style={btn(p, 'primary')}><Icon d={I.upload} /> Upload documents</button>}
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '40px 24px', textAlign: 'center', fontSize: 13, color: p.slate }}>
                No documents match these filters.{' '}
                <button onClick={clearFilters} style={{ ...btn(p, 'ghost', 'sm'), color: p.blue, padding: 0 }}>Clear filters</button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                  <thead>
                    <tr>
                      <th style={th}>Document</th>
                      <th style={th}>Type</th>
                      <th style={th}>Status</th>
                      <th style={th}>Source</th>
                      <th style={th}>Evidence for</th>
                      <th style={th}>Uploaded</th>
                      <th style={{ ...th, width: 44 }} aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(({ doc, status }) => {
                      const sel = selectedId === doc.id && panelVisible;
                      const questionnaires = new Set(doc.usage.map(u => u.response_id)).size;
                      return (
                        <tr key={doc.id} data-doc-row onClick={() => openDoc(doc.id)} className="vd-row"
                          style={{ cursor: 'pointer', background: sel ? soft(p, p.blue) : undefined }}>
                          <td style={td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                              <FileBadge name={doc.file_name} contentType={doc.content_type} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ fontSize: 13, fontWeight: 600, color: p.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{doc.file_name}</span>
                                  {doc.security_flags && (
                                    <span title={`Contains ${doc.security_flags}. Accepted, but open it with care.`} aria-label="Contains active content" style={{ color: p.amber, display: 'inline-flex', flexShrink: 0 }}><Icon d={I.alert} size={13} /></span>
                                  )}
                                </div>
                                <div style={{ fontSize: 11.5, color: p.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
                                  {doc.description || fmtSize(doc.size_bytes)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={td}>{doc.document_type ? <span style={{ fontSize: 12, fontWeight: 600, color: p.ink }}>{doc.document_type}</span> : <span style={{ color: p.muted }}>Untyped</span>}</td>
                          <td style={td}>
                            <Pill p={p} color={status.color}>{status.short}</Pill>
                            {status.key !== 'none' && <div style={{ fontSize: 11, color: p.muted, marginTop: 4 }}>{status.key === 'expiring' ? status.label : fmtDay(doc.expires_at)}</div>}
                          </td>
                          <td style={td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 24, height: 24, borderRadius: 99, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                background: soft(p, doc.source === 'public' ? p.green : doc.source === 'vendor' ? p.violet : p.blue),
                                color: doc.source === 'public' ? p.green : doc.source === 'vendor' ? p.violet : p.blue }}>
                                <Icon d={doc.source === 'public' ? I.globe : doc.source === 'vendor' ? I.building : I.user} size={12} />
                              </span>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 12.5, color: p.ink, fontWeight: 500 }}>
                                  {doc.source === 'public' ? 'Public document' : doc.uploaded_by_name || (doc.source === 'vendor' ? 'Vendor' : 'Team member')}
                                </div>
                                {doc.source === 'public' && doc.source_url ? (
                                  <a href={doc.source_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                                    style={{ fontSize: 11, color: p.blue, textDecoration: 'none' }}>View source ↗</a>
                                ) : (
                                  <div style={{ fontSize: 11, color: p.muted }}>{doc.source === 'vendor' ? 'Vendor portal' : 'Your team'}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td style={td}>
                            {doc.usage.length ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: p.ink, fontWeight: 500 }}>
                                <span style={{ color: p.blue, display: 'inline-flex' }}><Icon d={I.link} size={12} /></span>
                                {plural(doc.usage.length, 'question')}
                                <span style={{ color: p.muted, fontWeight: 400 }}>· {plural(questionnaires, 'questionnaire')}</span>
                              </span>
                            ) : <span style={{ color: p.muted }}>Not linked</span>}
                          </td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(doc.created_at)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            <button onClick={e => { e.stopPropagation(); downloadDoc(vendorId, doc); }} title="Download" aria-label={`Download ${doc.file_name}`}
                              className="vd-icon" style={{ width: 30, height: 30, borderRadius: 7, border: 'none', background: 'transparent', color: p.slate, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Icon d={I.download} size={15} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {docs.length > 0 && (
              <div style={{ padding: '10px 16px', fontSize: 11.5, color: p.muted, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <span>Showing {filtered.length} of {plural(docs.length, 'document')}</span>
                {filtersActive && <button onClick={clearFilters} style={{ ...btn(p, 'ghost', 'sm'), color: p.blue, padding: 0, fontSize: 11.5 }}>Clear filters</button>}
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        .vd-row:hover { background: ${p.hover}; }
        .vd-icon:hover { background: ${p.surface2} !important; color: ${p.ink} !important; }
        @keyframes vdPanelIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes vdPanelOut { from { transform: translateX(0); } to { transform: translateX(100%); } }
        @media (prefers-reduced-motion: reduce) { aside[data-doc-panel] { animation-duration: 1ms !important; } }
      `}</style>

      {selected && (
        <DocumentPanel
          key={selected.id} p={p} vendorId={vendorId} doc={selected} types={types} visible={panelVisible}
          canEdit={canEdit} canDelete={canDelete} docsKey={docsKey}
          onClose={closePanel} onDelete={() => setDeleting(selected)}
        />
      )}
      {upload && <UploadModal p={p} vendorId={vendorId} types={types} presetType={upload.presetType} docsKey={docsKey} onClose={() => setUpload(null)} />}
      {deleting && <DeleteDialog p={p} vendorId={vendorId} doc={deleting} docsKey={docsKey} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); setPanelVisible(false); setSelectedId(null); }} />}
    </div>
  );
}

/* ─── detail panel ────────────────────────────────────────────────────────────────────────── */

function DocumentPanel({ p, vendorId, doc, types, visible, canEdit, canDelete, docsKey, onClose, onDelete }: {
  p: Pal; vendorId: string; doc: Doc; types: string[]; visible: boolean; canEdit: boolean; canDelete: boolean;
  docsKey: unknown[]; onClose: () => void; onDelete: () => void;
}) {
  const panelRef = useRef<HTMLElement>(null);
  const status = statusOf(doc, p);
  const kind = fileKind(doc.file_name, doc.content_type);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ file_name: doc.file_name, document_type: doc.document_type || '', issued_at: doc.issued_at || '', expires_at: doc.expires_at || '', description: doc.description || '' });
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<{ url?: string; text?: string; error?: string } | null>(null);
  // Files carrying active content (PDF JavaScript, attachments) aren't rendered until asked.
  const [previewWanted, setPreviewWanted] = useState(!doc.security_flags);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (panelRef.current?.contains(target) || target.closest('[data-doc-row], [role="dialog"], [data-doc-modal]')) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('[data-doc-modal]')) onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  // Load an inline preview for PDFs, images and text files.
  useEffect(() => {
    if (!kind.preview || !previewWanted) return;
    let url: string | undefined;
    let cancelled = false;
    api.get(`/v1/vendors/${vendorId}/evidence-documents/${doc.id}/download`, { responseType: 'blob' })
      .then(async res => {
        if (cancelled) return;
        if (kind.preview === 'text') { setPreview({ text: (await (res.data as Blob).text()).slice(0, 20000) }); return; }
        const blob = kind.preview === 'pdf' ? new Blob([res.data], { type: 'application/pdf' }) : res.data;
        url = URL.createObjectURL(blob);
        setPreview({ url });
      })
      .catch(err => !cancelled && setPreview({ error: apiError(err, 'Preview unavailable') }));
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [vendorId, doc.id, kind.preview, previewWanted]);

  const saveMutation = useMutation({
    mutationFn: async () => api.patch(`/v1/vendors/${vendorId}/evidence-documents/${doc.id}`, {
      file_name: form.file_name, document_type: form.document_type || null, description: form.description || null,
      issued_at: form.issued_at || null, expires_at: form.expires_at || null,
    }),
    meta: { invalidates: [docsKey] },
    onSuccess: () => { setEditing(false); setError(''); },
    onError: err => setError(apiError(err, 'Could not save. Try again.')),
  });

  const startEdit = () => {
    setForm({ file_name: doc.file_name, document_type: doc.document_type || '', issued_at: doc.issued_at || '', expires_at: doc.expires_at || '', description: doc.description || '' });
    setError('');
    setEditing(true);
  };
  const suggestion = form.issued_at && VALIDITY_MONTHS[form.document_type] ? addMonths(form.issued_at, VALIDITY_MONTHS[form.document_type]) : null;

  const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, fontFamily: sans, color: p.ink, background: p.surface, border: `1px solid ${p.line}`, borderRadius: 7, outline: 'none' };
  const label: React.CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 600, color: p.slate, marginBottom: 5 };
  const section = (title: string, extra?: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: p.muted }}>{title}</span>
      <span style={{ flex: 1 }} />
      {extra}
    </div>
  );
  const row = (k: string, v: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12.5, padding: '7px 0', borderBottom: `1px solid ${p.surface2}` }}>
      <span style={{ color: p.muted }}>{k}</span>
      <span style={{ color: p.ink, textAlign: 'right', minWidth: 0, wordBreak: 'break-word' }}>{v}</span>
    </div>
  );
  const types2 = form.document_type && !types.includes(form.document_type) ? [form.document_type, ...types] : types;
  const toneColor = (t: 'green' | 'amber' | 'red' | 'slate') => (t === 'green' ? p.green : t === 'amber' ? p.amber : t === 'red' ? p.red : p.slate);

  return (
    <aside ref={panelRef} data-doc-panel aria-label={`${doc.file_name} details`} style={{
      position: 'fixed', top: APP_HEADER_HEIGHT, right: 0, bottom: 0, width: 'min(520px, 100vw)', zIndex: 1150, background: p.surface,
      borderLeft: `1px solid ${p.line}`, display: 'flex', flexDirection: 'column', fontFamily: sans,
      animation: `${visible ? 'vdPanelIn' : 'vdPanelOut'} ${PANEL_MS}ms ${visible ? 'ease-out' : 'ease-in'} forwards`,
    }}>
      {/* header */}
      <div style={{ padding: '16px 18px', borderBottom: `1px solid ${p.line}`, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <FileBadge name={doc.file_name} contentType={doc.content_type} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: p.ink, wordBreak: 'break-word', lineHeight: 1.35 }}>{doc.file_name}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
            <Pill p={p} color={status.color}>{status.label}</Pill>
            {doc.document_type && <Pill p={p} color={p.blue} dot={false}>{doc.document_type}</Pill>}
          </div>
        </div>
        <button onClick={onClose} aria-label="Close" className="vd-icon" style={{ width: 30, height: 30, borderRadius: 7, border: 'none', background: 'transparent', color: p.slate, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon d={I.close} />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px 24px', display: 'flex', flexDirection: 'column', gap: 22 }}>
        {doc.security_flags && (
          <div style={{ display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 9, background: soft(p, p.amber), color: p.amber, fontSize: 12.5, lineHeight: 1.5 }}>
            <Icon d={I.shield} size={15} />
            <span>
              <strong>Contains {doc.security_flags}.</strong> This is common in legitimate vendor documents (AWS SOC 2 PDFs carry JavaScript, SIG questionnaires
              carry macros), so it was accepted. It can also be used to run code, though: keep macros and scripts disabled when you open it unless you trust the source.
            </span>
          </div>
        )}
        {(status.key === 'expired' || status.key === 'expiring') && (
          <div style={{ display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 9, background: soft(p, status.color), color: status.color, fontSize: 12.5, lineHeight: 1.5 }}>
            <Icon d={status.key === 'expired' ? I.alert : I.clock} size={15} />
            <span>{status.key === 'expired' ? 'This document has expired. Ask the vendor for a current copy before relying on it.' : `${status.label}. Request the renewed version from the vendor ahead of time.`}</span>
          </div>
        )}

        {/* preview */}
        <div>
          {section('Preview')}
          <div style={{ border: `1px solid ${p.line}`, borderRadius: 9, overflow: 'hidden', background: p.surface2 }}>
            {!kind.preview ? (
              <div style={{ padding: '26px 16px', textAlign: 'center' }}>
                <FileBadge name={doc.file_name} contentType={doc.content_type} size={40} />
                <div style={{ fontSize: 12.5, color: p.slate, marginTop: 10 }}>Preview isn't available for {kind.label} files.</div>
                <button onClick={() => downloadDoc(vendorId, doc)} style={{ ...btn(p, 'secondary', 'sm'), marginTop: 10 }}><Icon d={I.download} size={13} /> Download to view</button>
              </div>
            ) : !previewWanted ? (
              <div style={{ padding: '26px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 12.5, color: p.slate, lineHeight: 1.5 }}>Preview is off for this file because it contains {doc.security_flags}.</div>
                <button onClick={() => setPreviewWanted(true)} style={{ ...btn(p, 'secondary', 'sm'), marginTop: 10 }}>Show preview anyway</button>
              </div>
            ) : !preview ? (
              <div style={{ padding: '40px 0', textAlign: 'center', fontSize: 12.5, color: p.muted }}>Loading preview…</div>
            ) : preview.error ? (
              <div style={{ padding: '30px 16px', textAlign: 'center', fontSize: 12.5, color: p.red }}>{preview.error}</div>
            ) : preview.text !== undefined ? (
              <pre style={{ margin: 0, padding: 14, maxHeight: 320, overflow: 'auto', fontSize: 12, lineHeight: 1.55, color: p.ink, whiteSpace: 'pre-wrap', fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>{preview.text}</pre>
            ) : kind.preview === 'image' ? (
              <img src={preview.url} alt={doc.file_name} style={{ display: 'block', maxWidth: '100%', maxHeight: 360, margin: '0 auto' }} />
            ) : (
              <iframe src={preview.url} title={doc.file_name} style={{ display: 'block', width: '100%', height: 380, border: 'none', background: '#fff' }} />
            )}
          </div>
        </div>

        {/* details */}
        <div>
          {section('Details', canEdit && !editing && <button onClick={startEdit} style={{ ...btn(p, 'ghost', 'sm'), color: p.blue }}><Icon d={I.edit} size={12} /> Edit</button>)}
          {editing ? (
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <span style={label}>Name</span>
                <input value={form.file_name} onChange={e => setForm({ ...form, file_name: e.target.value })} style={input} />
              </div>
              <div>
                <span style={label}>Document type</span>
                <select value={form.document_type} onChange={e => setForm({ ...form, document_type: e.target.value })} style={{ ...input, cursor: 'pointer' }}>
                  <option value="">Untyped</option>
                  {types2.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <span style={label}>Issue date</span>
                  <input type="date" value={form.issued_at} onChange={e => setForm({ ...form, issued_at: e.target.value })} style={input} />
                </div>
                <div>
                  <span style={label}>Expiry date</span>
                  <input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} style={input} />
                </div>
              </div>
              {(suggestion || form.expires_at) && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: -4 }}>
                  {suggestion && suggestion !== form.expires_at && (
                    <button onClick={() => setForm({ ...form, expires_at: suggestion })} style={{ ...btn(p, 'secondary', 'sm'), color: p.blue }}>
                      Use {VALIDITY_MONTHS[form.document_type] / 12} year{VALIDITY_MONTHS[form.document_type] === 12 ? '' : 's'} from issue · {fmtDay(suggestion)}
                    </button>
                  )}
                  {form.expires_at && <button onClick={() => setForm({ ...form, expires_at: '' })} style={btn(p, 'ghost', 'sm')}>No expiry</button>}
                </div>
              )}
              <div>
                <span style={label}>Description</span>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder="E.g. Type II report covering Jan–Dec 2025, issued by Deloitte" style={{ ...input, resize: 'vertical', lineHeight: 1.5 }} />
              </div>
              {error && <div style={{ fontSize: 12.5, color: p.red }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setEditing(false)} disabled={saveMutation.isPending} style={btn(p)}>Cancel</button>
                <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} style={{ ...btn(p, 'primary'), opacity: saveMutation.isPending ? 0.6 : 1 }}>{saveMutation.isPending ? 'Saving…' : 'Save changes'}</button>
              </div>
            </div>
          ) : (
            <div>
              {row('Type', doc.document_type || <span style={{ color: p.muted }}>Untyped</span>)}
              {row('Issued', doc.issued_at ? fmtDay(doc.issued_at) : <span style={{ color: p.muted }}>Not set</span>)}
              {row('Expires', doc.expires_at ? <span style={{ color: status.color, fontWeight: 600 }}>{fmtDay(doc.expires_at)}</span> : <span style={{ color: p.muted }}>No expiry set</span>)}
              {row('Size', fmtSize(doc.size_bytes))}
              {row('Uploaded by', `${doc.uploaded_by_name || (doc.source === 'vendor' ? 'Vendor' : 'Team member')} · ${doc.source === 'vendor' ? 'vendor portal' : 'your team'}`)}
              {row('Uploaded on', fmtDate(doc.created_at))}
              {doc.updated_at && row('Last edited', fmtDate(doc.updated_at))}
              {doc.description && (
                <div style={{ fontSize: 12.5, color: p.ink, lineHeight: 1.6, marginTop: 10, padding: '10px 12px', background: p.surface2, borderRadius: 8, whiteSpace: 'pre-wrap' }}>{doc.description}</div>
              )}
              {canEdit && !doc.expires_at && (
                <button onClick={startEdit} style={{ ...btn(p, 'ghost', 'sm'), color: p.blue, paddingLeft: 0, marginTop: 8 }}><Icon d={I.clock} size={12} /> Set an expiry date to track renewals</button>
              )}
            </div>
          )}
        </div>

        {/* usage */}
        <div>
          {section(`Used as evidence (${doc.usage.length})`)}
          {doc.usage.length === 0 ? (
            <div style={{ fontSize: 12.5, color: p.muted, padding: '12px 14px', border: `1px dashed ${p.lineStrong}`, borderRadius: 9, lineHeight: 1.5 }}>
              Not attached to any questionnaire answer yet. The vendor can attach it from their questionnaire page, where it's available in their document library.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {doc.usage.map(u => {
                const v = u.verdict ? VERDICTS[u.verdict] : null;
                return (
                  <div key={`${u.response_id}-${u.item_id}`} style={{ border: `1px solid ${p.line}`, borderRadius: 9, padding: '10px 12px' }}>
                    <div style={{ fontSize: 11, color: p.muted, marginBottom: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, color: p.slate }}>{u.questionnaire_name}</span>· sent {fmtDate(u.sent_at)}
                    </div>
                    <div style={{ fontSize: 12.5, color: p.ink, lineHeight: 1.5 }}>{u.question_text}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 11.5, color: p.slate }}>Answer: <strong style={{ color: p.ink }}>{(u.answer_value || '—').replace(/\n/g, ', ')}</strong></span>
                      <span style={{ flex: 1 }} />
                      {v ? (
                        <span title={u.rationale || undefined}><Pill p={p} color={toneColor(v.tone)}>AI check: {v.label}{u.confidence != null ? ` · ${u.confidence}%` : ''}</Pill></span>
                      ) : <Pill p={p} color={p.muted}>AI check: not run</Pill>}
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: p.muted, lineHeight: 1.5 }}>The AI check judges all of a question's attached evidence together, not this file alone.</div>
            </div>
          )}
        </div>
      </div>

      {/* footer */}
      <div style={{ padding: '12px 18px', borderTop: `1px solid ${p.line}`, display: 'flex', gap: 8 }}>
        <button onClick={() => downloadDoc(vendorId, doc)} style={{ ...btn(p, 'primary'), flex: 1 }}><Icon d={I.download} /> Download</button>
        {canDelete && <button onClick={onDelete} style={{ ...btn(p), color: p.red }}><Icon d={I.trash} /> Delete</button>}
      </div>
    </aside>
  );
}

/* ─── upload modal ────────────────────────────────────────────────────────────────────────── */

interface Pending { key: string; file: File; type: string; issued: string; expires: string; state: 'ready' | 'uploading' | 'done' | 'error'; error?: string }

function UploadModal({ p, vendorId, types, presetType, docsKey, onClose }: {
  p: Pal; vendorId: string; types: string[]; presetType?: string; docsKey: unknown[]; onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<Pending[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = (f: File) => {
    if (!ALLOWED_EXT.includes(extOf(f.name))) return `${extOf(f.name) || 'This file type'} isn't supported`;
    if (f.size > MAX_SIZE) return 'Larger than 15 MB';
    if (f.size === 0) return 'File is empty';
    return undefined;
  };
  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const next = Array.from(list).map(f => {
      const err = validate(f);
      return { key: Math.random().toString(36).slice(2), file: f, type: presetType || guessType(f.name), issued: '', expires: '', state: err ? 'error' : 'ready', error: err } as Pending;
    });
    setFiles(cur => [...cur, ...next]);
  };
  const update = (key: string, patch: Partial<Pending>) => setFiles(cur => cur.map(f => (f.key === key ? { ...f, ...patch } : f)));

  const ready = files.filter(f => f.state === 'ready');
  const allDone = files.length > 0 && files.every(f => f.state === 'done' || (f.state === 'error' && !!validate(f.file)));

  const uploadAll = async () => {
    setBusy(true);
    for (const f of ready) {
      if (f.issued && f.expires && f.expires < f.issued) { update(f.key, { state: 'error', error: 'Expiry is before the issue date' }); continue; }
      update(f.key, { state: 'uploading', error: undefined });
      const form = new FormData();
      form.append('file', f.file);
      form.append('document_type', f.type);
      if (f.issued) form.append('issued_at', f.issued);
      if (f.expires) form.append('expires_at', f.expires);
      try {
        await api.post(`/v1/vendors/${vendorId}/evidence-documents`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
        update(f.key, { state: 'done' });
      } catch (err) {
        update(f.key, { state: 'error', error: apiError(err, 'Upload failed') });
      }
    }
    setBusy(false);
    queryClient.invalidateQueries({ queryKey: docsKey });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const input: React.CSSProperties = { boxSizing: 'border-box', padding: '6px 8px', fontSize: 12, fontFamily: sans, color: p.ink, background: p.surface, border: `1px solid ${p.line}`, borderRadius: 6, outline: 'none' };

  return (
    <div data-doc-modal onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label="Upload documents" style={{ width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: p.surface, border: `1px solid ${p.line}`, borderRadius: 14, fontFamily: sans, boxShadow: '0 20px 50px rgba(0,0,0,.25)' }}>
        <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${p.line}`, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: p.ink }}>Upload documents</div>
            <div style={{ fontSize: 12.5, color: p.slate, marginTop: 3 }}>PDF, Word, Excel, images or text, up to 15 MB each. Every file is checked on upload. Set an expiry date to get renewal tracking.</div>
          </div>
          <button onClick={() => !busy && onClose()} aria-label="Close" className="vd-icon" style={{ width: 30, height: 30, borderRadius: 7, border: 'none', background: 'transparent', color: p.slate, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon d={I.close} /></button>
        </div>

        <div style={{ padding: '16px 22px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            style={{ border: `2px dashed ${dragOver ? p.primary : p.lineStrong}`, background: dragOver ? soft(p, p.primary) : p.surface2, borderRadius: 12, padding: files.length ? '18px' : '36px 18px', textAlign: 'center', cursor: 'pointer', transition: 'all .12s' }}>
            <span style={{ width: 42, height: 42, borderRadius: 12, background: soft(p, p.blue), color: p.blue, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              <Icon d={I.upload} size={20} />
            </span>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: p.ink }}>Drop files here or <span style={{ color: p.blue }}>browse</span></div>
            <div style={{ fontSize: 11.5, color: p.muted, marginTop: 3 }}>You can add several at once — the type is guessed from each file name.</div>
            <input ref={inputRef} type="file" multiple accept={ALLOWED_EXT.join(',')} style={{ display: 'none' }} onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          </div>

          {files.map(f => {
            const suggestion = f.issued && VALIDITY_MONTHS[f.type] ? addMonths(f.issued, VALIDITY_MONTHS[f.type]) : null;
            const locked = f.state === 'uploading' || f.state === 'done' || !!validate(f.file);
            return (
              <div key={f.key} style={{ border: `1px solid ${f.state === 'error' ? `${p.red}60` : p.line}`, borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <FileBadge name={f.file.name} contentType={f.file.type} size={32} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: p.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</div>
                    <div style={{ fontSize: 11.5, color: f.state === 'error' ? p.red : f.state === 'done' ? p.green : p.muted }}>
                      {f.state === 'error' ? f.error : f.state === 'done' ? 'Uploaded' : f.state === 'uploading' ? 'Uploading…' : fmtSize(f.file.size)}
                    </div>
                  </div>
                  {f.state === 'done' ? <span style={{ color: p.green, display: 'inline-flex' }}><Icon d={I.check} size={16} sw={2.6} /></span>
                    : f.state !== 'uploading' && (
                      <button onClick={() => setFiles(cur => cur.filter(x => x.key !== f.key))} disabled={busy} aria-label="Remove file" className="vd-icon"
                        style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: 'transparent', color: p.slate, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon d={I.close} size={13} /></button>
                    )}
                </div>
                {!validate(f.file) && f.state !== 'done' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) 1fr 1fr', gap: 8, marginTop: 10 }}>
                    <label style={{ fontSize: 11, color: p.muted, fontWeight: 600 }}>Type
                      <select value={f.type} disabled={locked} onChange={e => update(f.key, { type: e.target.value })} style={{ ...input, width: '100%', marginTop: 3, cursor: 'pointer' }}>
                        {types.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                    <label style={{ fontSize: 11, color: p.muted, fontWeight: 600 }}>Issued
                      <input type="date" value={f.issued} disabled={locked} onChange={e => update(f.key, { issued: e.target.value })} style={{ ...input, width: '100%', marginTop: 3 }} />
                    </label>
                    <label style={{ fontSize: 11, color: p.muted, fontWeight: 600 }}>Expires
                      <input type="date" value={f.expires} disabled={locked} onChange={e => update(f.key, { expires: e.target.value })} style={{ ...input, width: '100%', marginTop: 3 }} />
                    </label>
                    {suggestion && suggestion !== f.expires && !locked && (
                      <button onClick={() => update(f.key, { expires: suggestion })} style={{ ...btn(p, 'ghost', 'sm'), color: p.blue, gridColumn: '1 / -1', justifySelf: 'start', paddingLeft: 0 }}>
                        Set expiry to {fmtDay(suggestion)} ({VALIDITY_MONTHS[f.type] / 12} year{VALIDITY_MONTHS[f.type] === 12 ? '' : 's'} after issue, typical for a {f.type})
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${p.line}`, background: p.surface2, borderRadius: '0 0 14px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: p.muted }}>{files.length ? `${files.filter(f => f.state === 'done').length} of ${files.length} uploaded` : 'No files selected'}</span>
          <span style={{ flex: 1 }} />
          {allDone ? (
            <button onClick={onClose} style={btn(p, 'primary')}>Done</button>
          ) : (
            <>
              <button onClick={onClose} disabled={busy} style={btn(p)}>Cancel</button>
              <button onClick={uploadAll} disabled={busy || ready.length === 0} style={{ ...btn(p, 'primary'), opacity: busy || ready.length === 0 ? 0.55 : 1 }}>
                <Icon d={I.upload} /> {busy ? 'Uploading…' : `Upload ${ready.length ? plural(ready.length, 'file') : ''}`.trim()}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── delete dialog ───────────────────────────────────────────────────────────────────────── */

function DeleteDialog({ p, vendorId, doc, docsKey, onClose, onDeleted }: {
  p: Pal; vendorId: string; doc: Doc; docsKey: unknown[]; onClose: () => void; onDeleted: () => void;
}) {
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: () => api.delete(`/v1/vendors/${vendorId}/evidence-documents/${doc.id}`),
    meta: { invalidates: [docsKey] },
    onSuccess: onDeleted,
    onError: err => setError(apiError(err, 'Could not delete this document. Try again.')),
  });
  const questionnaires = new Set(doc.usage.map(u => u.response_id)).size;
  return (
    <div data-doc-modal onMouseDown={e => { if (e.target === e.currentTarget && !mutation.isPending) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label="Delete document" style={{ width: '100%', maxWidth: 440, background: p.surface, border: `1px solid ${p.line}`, borderRadius: 12, padding: 22, fontFamily: sans }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ width: 36, height: 36, borderRadius: 10, background: soft(p, p.red), color: p.red, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon d={I.trash} size={17} /></span>
          <div>
            <div style={{ fontSize: 15.5, fontWeight: 700, color: p.ink }}>Delete document?</div>
            <div style={{ fontSize: 13, color: p.slate, lineHeight: 1.55, marginTop: 4 }}>
              <strong style={{ color: p.ink }}>{doc.file_name}</strong> will be permanently deleted. This can't be undone.
            </div>
            {doc.usage.length > 0 && (
              <div style={{ fontSize: 12.5, color: p.amber, background: soft(p, p.amber), borderRadius: 8, padding: '8px 10px', marginTop: 10, lineHeight: 1.5 }}>
                It's attached as evidence to {plural(doc.usage.length, 'question')} on {plural(questionnaires, 'questionnaire')}. Those answers will lose this evidence.
              </div>
            )}
          </div>
        </div>
        {error && <div style={{ fontSize: 12.5, color: p.red, marginTop: 12 }} role="alert">{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button onClick={onClose} disabled={mutation.isPending} style={btn(p)}>Cancel</button>
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending} style={{ ...btn(p, 'danger'), opacity: mutation.isPending ? 0.7 : 1 }}>{mutation.isPending ? 'Deleting…' : 'Delete document'}</button>
        </div>
      </div>
    </div>
  );
}
